import { supabase } from '@/integrations/supabase/client';
import { isSessionDefinitivelyInvalid, refreshSessionShared, signOutLocal } from '@/services/auth/sessionManager';

/**
 * Erreur normalisée renvoyée par `invokeWithRetry`.
 *
 * Pourquoi ne pas propager l'objet brut de supabase-js ? Parce que sur une
 * erreur HTTP il expose un `FunctionsHttpError` dont :
 * - le statut n'est pas sur `error.status` mais sur `error.context.status`
 *   (les tests `error.status === 401` de l'ancienne version ne matchaient donc
 *   jamais, et la logique de retry / de déconnexion était morte) ;
 * - le corps de la réponse est une `Response` non consommée, lisible une seule
 *   fois, donc inexploitable par les appelants pour retrouver un code d'erreur
 *   métier (ex. `NO_VALID_SUBSCRIPTION_FOR_DATE`).
 *
 * On normalise donc une bonne fois pour toutes : `status` renseigné, corps déjà
 * lu et exposé en texte sur `body` ET sur `context.body` / `context.responseText`
 * (forme attendue par les appelants existants, cf. `bookingsRpcWrapper`).
 */
export interface InvokeError extends Error {
  /** Statut HTTP quand il est connu (undefined pour une panne réseau). */
  status?: number;
  /**
   * `true` quand l'échec est un dépassement du délai client.
   *
   * Volontairement SANS `status` : aucune réponse HTTP n'a été reçue.
   * ⚠️ Un timeout n'est PAS équivalent à une panne réseau : le réseau coupé
   * signifie « la requête n'est probablement jamais partie / n'ira jamais
   * jusqu'au bout », alors qu'un timeout signifie « le serveur est peut-être
   * TOUJOURS en train d'exécuter la requête ». Les deux se traduisent par
   * `status === undefined`, d'où ce drapeau : il permet à
   * `isLocalWriteFallbackAllowed` d'interdire le repli en ÉCRITURE sur un
   * timeout (risque de doublon) tout en le laissant ouvert sur une panne
   * réseau franche.
   */
  timedOut?: boolean;
  /** Corps de la réponse d'erreur, déjà lu, en texte. */
  body?: string;
  context?: {
    status?: number;
    body?: string;
    responseText?: string;
    /** Objet `context` d'origine (Response) conservé pour diagnostic. */
    response?: unknown;
  };
  /** Erreur d'origine, conservée pour le diagnostic. */
  originalError?: unknown;
}

export interface InvokeWithRetryResult<T = any> {
  data: T | null;
  /**
   * Toute défaillance ressort ici : réseau, HTTP non-2xx, ou erreur métier
   * renvoyée en HTTP 200 dans `{ error: "…" }`. `null` uniquement en succès.
   */
  error: InvokeError | null;
  /**
   * 401 persistant : la session n'est plus valide, la déconnexion et la
   * redirection ont déjà été déclenchées. `error` est renseigné en parallèle,
   * pour que les appelants qui ne testent que `error` échouent quand même.
   */
  authExpired?: boolean;
  /**
   * 403 : l'utilisateur est authentifié mais n'a pas les droits (les edge
   * functions renvoient 403 pour « Admin role required », « Super admin access
   * required »…). Surtout ne pas déconnecter dans ce cas.
   */
  forbidden?: boolean;
}

/** Le statut HTTP est tantôt sur l'erreur, tantôt sur son `context` (Response). */
function extractStatus(raw: any): number | undefined {
  if (typeof raw?.status === 'number') return raw.status;
  const ctx = raw?.context;
  if (ctx && typeof ctx.status === 'number') return ctx.status;
  return undefined;
}

/**
 * Lit le corps de la réponse d'erreur. On clone la `Response` : son flux n'est
 * consommable qu'une fois, et un appelant pourrait vouloir la relire.
 */
async function readErrorBody(raw: any): Promise<string> {
  const ctx = raw?.context;
  if (!ctx) return '';
  if (typeof ctx === 'string') return ctx;
  if (typeof Response !== 'undefined' && ctx instanceof Response) {
    try {
      return await ctx.clone().text();
    } catch {
      return '';
    }
  }
  return String(ctx.body ?? ctx.responseText ?? '');
}

/**
 * Les edge functions répondent `{ error: "…" }`. Le message par défaut de
 * supabase-js (« Edge Function returned a non-2xx status code ») n'est pas
 * affichable à l'utilisateur : on lui préfère le message réel du serveur.
 */
function messageFromBody(body: string): string | undefined {
  if (!body) return undefined;
  try {
    const parsed = JSON.parse(body);
    const candidate = parsed?.error ?? parsed?.message;
    if (typeof candidate === 'string' && candidate.trim()) return candidate;
  } catch {
    // Corps non JSON (page HTML d'erreur du proxy, texte brut) : on l'ignore.
  }
  return undefined;
}

function buildInvokeError(
  functionName: string,
  raw: any,
  status: number | undefined,
  body: string,
  fallbackMessage: string
): InvokeError {
  const message = messageFromBody(body) || raw?.message || fallbackMessage;
  const error = new Error(message) as InvokeError;
  error.name = raw?.name || 'EdgeFunctionError';
  error.status = status;
  error.body = body;
  error.context = {
    status,
    body,
    responseText: body,
    response: raw?.context,
  };
  error.originalError = raw;
  return error;
}

async function normalizeError(functionName: string, raw: any): Promise<InvokeError> {
  const status = extractStatus(raw);
  const body = await readErrorBody(raw);
  return buildInvokeError(
    functionName,
    raw,
    status,
    body,
    `[${functionName}] Edge function call failed`
  );
}

/**
 * Détecte une erreur métier renvoyée en HTTP 200 (`{ error: "…" }`).
 * Sans ça, l'appelant reçoit `error: null` et traite l'échec en succès.
 */
function businessErrorFromPayload(functionName: string, data: any): InvokeError | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const businessError = (data as { error?: unknown }).error;
  if (!businessError) return null;

  const message =
    typeof businessError === 'string'
      ? businessError
      : (data as { message?: unknown }).message != null
        ? String((data as { message?: unknown }).message)
        : JSON.stringify(businessError);

  let body = '';
  try {
    body = JSON.stringify(data);
  } catch {
    body = String(message);
  }

  return buildInvokeError(
    functionName,
    { message },
    200,
    body,
    `[${functionName}] Edge function returned a business error`
  );
}

/**
 * Délai maximum par tentative, en millisecondes.
 *
 * Sans plafond, un appel qui ne répond jamais (proxy silencieux, réseau mobile
 * qui « raccroche » sans FIN, edge function qui boucle) laisse l'écran en
 * chargement indéfiniment : c'est le trou que la copie privée d'invokeWithRetry
 * de `ForgotPassword` colmatait localement avec 15 s.
 *
 * 30 s plutôt que 15 s parce que ce plafond s'applique désormais à TOUS les
 * appels, y compris ceux qui n'avaient aucune limite jusqu'ici. Les deux plus
 * lourds ne s'en contentent pas et relèvent leur propre `timeoutMs` à 60 s
 * (`coach-analytics` : 7 RPC d'agrégation ; `dashboard-kpis` : 11 actions en
 * parallèle au chargement du tableau de bord). `get-tenant-stats` relit une
 * table de cache et reste au défaut.
 *
 * Budget d'un appel qui ne répond jamais : `timeoutMs`, pas trois fois
 * `timeoutMs` — un dépassement de délai n'est plus rejoué (cf.
 * `options.retryOnTimeout`), justement parce que la tentative précédente peut
 * être encore en cours côté serveur.
 */
export const DEFAULT_INVOKE_TIMEOUT_MS = 30_000;

/**
 * Base du backoff entre deux tentatives : 1 s après la 1re tentative, 2 s après
 * la 2e. Le premier délai est identique à l'ancien délai fixe de 1 s ; seul le
 * second s'allonge, et uniquement sur un appel qui échoue déjà.
 */
const RETRY_BACKOFF_BASE_MS = 1_000;

/**
 * Backoff progressif : `attempt` est l'index (0-based) de la tentative échouée.
 * Reprend à l'identique la formule de la copie privée de `ForgotPassword`
 * (`1000 * (attempt + 1)`), pour ne pas modifier le rythme de relance de la page.
 */
function backoffDelay(attempt: number): number {
  return RETRY_BACKOFF_BASE_MS * (attempt + 1);
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export interface InvokeWithRetryOptions {
  /**
   * Délai maximum par tentative, en ms. Défaut : {@link DEFAULT_INVOKE_TIMEOUT_MS}.
   * `0` (ou une valeur non finie) désactive complètement le plafond.
   */
  timeoutMs?: number;
  /**
   * Désactive TOUTE la gestion de session : pas de `refreshSession` sur 401,
   * pas de `signOut`, pas de redirection vers `/login`, et pas de drapeau
   * `authExpired` dans le résultat.
   *
   * À utiliser sur les écrans atteignables SANS session (mot de passe oublié,
   * inscription, invitation…). Sur ces pages, un 401 ne veut pas dire « session
   * expirée » — il n'y a pas de session à rafraîchir, et rediriger vers
   * `/login` depuis un écran public produit au mieux du bruit, au pire une
   * boucle de redirection.
   *
   * Le 401 est alors traité comme n'importe quelle erreur HTTP :
   * `{ data: null, error }` avec `error.status === 401`.
   */
  skipSessionHandling?: boolean;
  /**
   * Rejoue aussi les 5xx génériques (500/502/504), en plus du 503 déjà rejoué.
   *
   * Désactivé par défaut : un 500 peut signifier que la fonction a planté APRÈS
   * une écriture partielle, et rejouer une création n'est pas anodin. À n'activer
   * que sur des appels idempotents (envoi d'e-mail, lecture).
   */
  retryOnServerError?: boolean;
  /**
   * Rejoue l'appel après un dépassement du délai client.
   *
   * DÉSACTIVÉ PAR DÉFAUT, et c'est le point important. Un timeout ne dit pas
   * « le serveur n'a rien fait », il dit « le serveur ne m'a pas encore
   * répondu » : la fonction edge est peut-être toujours en cours d'exécution.
   * Rejouer une CRÉATION dans ces conditions lance une seconde exécution
   * concurrente de la première. Or les gardes anti-doublon des fonctions edge
   * sont des `SELECT` puis `INSERT` sans contrainte d'unicité en base
   * (cf. `createSubscription` dans `supabase/functions/subscriptions-management/index.ts`) :
   * deux exécutions concurrentes lisent toutes les deux « aucun abonnement
   * actif » et insèrent toutes les deux.
   *
   * Ne pas rejouer restitue exactement le comportement d'avant l'introduction
   * du timeout (un appel sans réponse ne se rejouait jamais, faute de se
   * terminer), tout en conservant le bénéfice recherché : on cesse d'attendre
   * et on rend la main avec une erreur au lieu de laisser un écran en
   * chargement indéfini. Effet de bord favorable : le budget d'un appel qui ne
   * répond pas retombe de ~93 s à `timeoutMs`.
   *
   * À n'activer que sur des appels sans effet de bord cumulatif observable
   * (envoi d'e-mail de réinitialisation, lecture).
   */
  retryOnTimeout?: boolean;
  /**
   * Appelé au début de chaque nouvelle tentative (`attempt` >= 1), avant l'appel
   * réseau et après le backoff. Permet à l'UI d'afficher « tentative 2/3 ».
   */
  onRetry?: (attempt: number) => void;
}

/**
 * Erreur de dépassement de délai. Aucun `status` n'est renseigné, à dessein :
 * `isTransportFailure` décide du droit au repli local à partir de la présence
 * d'un statut HTTP (`status === undefined` ⇒ le serveur n'a jamais pu se
 * prononcer ⇒ panne de transport). Poser un statut fictif (408, 504…) ferait
 * classer le timeout en décision serveur — pour 504 ce serait encore un 5xx donc
 * un repli autorisé, mais 408 basculerait en refus et interdirait le repli.
 *
 * Le drapeau `timedOut` reste donc le SEUL discriminant entre « pas de réponse
 * parce que le réseau est coupé » et « pas de réponse parce que le serveur est
 * lent » : `isLocalWriteFallbackAllowed` s'en sert pour fermer le repli en
 * écriture dans le second cas.
 */
function buildTimeoutError(functionName: string, timeoutMs: number): InvokeError {
  const error = buildInvokeError(
    functionName,
    { name: 'EdgeFunctionTimeoutError', message: `[${functionName}] Request timed out after ${timeoutMs}ms` },
    undefined,
    '',
    `[${functionName}] Request timeout`
  );
  error.timedOut = true;
  return error;
}

function isTimeoutError(err: unknown): err is InvokeError {
  return !!err && typeof err === 'object' && (err as InvokeError).timedOut === true;
}

/**
 * Plafonne la durée d'une tentative. Le `clearTimeout` est indispensable : sans
 * lui, chaque appel réussi laisse un timer actif (fuite en test, et process Node
 * maintenu en vie).
 *
 * L'appel HTTP sous-jacent n'est pas annulé (`supabase.functions.invoke`
 * n'accepte pas d'`AbortSignal`) : on cesse simplement de l'attendre.
 */
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, functionName: string): Promise<T> {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return promise;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(buildTimeoutError(functionName, timeoutMs)), timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

/**
 * Un 401 ne suffit PAS à conclure que la session est morte : après un
 * déploiement ou une coupure réseau, des appels légitimes remontent 401.
 * On ne déconnecte donc qu'après avoir confirmé, via `refreshSession()`, que le
 * jeton est réellement invalide.
 */
async function handleExpiredSession(functionName: string): Promise<boolean> {
  const invalid = await isSessionDefinitivelyInvalid();
  if (!invalid) {
    console.warn(`[${functionName}] 401 received but session not confirmed invalid, keeping user signed in`);
    return false;
  }

  console.log(`[${functionName}] Session confirmed invalid, initiating silent logout`);
  await signOutLocal();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
  return true;
}

/**
 * Invocation d'edge function avec retry et gestion de session.
 *
 * Contrat (uniforme, la fonction ne lève JAMAIS) :
 * - succès              → `{ data, error: null }`
 * - erreur réseau / HTTP → `{ data: null, error }`
 * - erreur métier en 200 → `{ data, error }` (les données brutes restent
 *   disponibles pour les appelants qui savent les lire)
 * - 401 persistant       → `{ data: null, error, authExpired: true }` après
 *   déconnexion silencieuse
 * - 403                  → `{ data: null, error, forbidden: true }`, SANS
 *   déconnexion : c'est un refus de droits, pas une session invalide
 *
 * Les anciennes versions levaient sur erreur serveur et renvoyaient
 * `error: null` sur session expirée ou sur erreur métier en 200 : le champ
 * `error` ne pouvait donc jamais être non nul, et toutes les branches
 * `if (error)` des appelants étaient du code mort.
 *
 * Chaque tentative est plafonnée par un délai ({@link DEFAULT_INVOKE_TIMEOUT_MS},
 * ajustable via `options.timeoutMs`) : le dépassement produit une erreur SANS
 * statut HTTP mais marquée `timedOut`. Elle n'est PAS rejouée (cf.
 * `options.retryOnTimeout`) et n'autorise PAS de repli en écriture directe
 * (cf. `isLocalWriteFallbackAllowed`), parce que la fonction edge peut être
 * encore en cours d'exécution côté serveur.
 *
 * @param functionName - Nom de l'edge function
 * @param body - Corps de la requête
 * @param maxRetries - Nombre de tentatives supplémentaires (défaut : 2)
 * @param options - Timeout, gestion de session, retour de progression
 */
export async function invokeWithRetry<T = any>(
  functionName: string,
  body: Record<string, any>,
  maxRetries = 2,
  options: InvokeWithRetryOptions = {}
): Promise<InvokeWithRetryResult<T>> {
  const {
    timeoutMs = DEFAULT_INVOKE_TIMEOUT_MS,
    skipSessionHandling = false,
    retryOnServerError = false,
    retryOnTimeout = false,
    onRetry,
  } = options;

  let lastError: InvokeError | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      onRetry?.(attempt);
      console.log(`[${functionName}] Retry attempt ${attempt}/${maxRetries}`);
    }

    try {
      const { data, error } = await withTimeout(
        supabase.functions.invoke(functionName, { body }),
        timeoutMs,
        functionName
      );

      if (error) {
        const normalized = await normalizeError(functionName, error);
        lastError = normalized;
        const status = normalized.status;

        // 401 = jeton absent/expiré : on tente un rafraîchissement puis on rejoue.
        //       Sans gestion de session, rien à rafraîchir : on ne rejoue pas.
        // 503 = maintenance (et 5xx si `retryOnServerError`) : on rejoue.
        const isRefreshableAuth = status === 401 && !skipSessionHandling;
        const isRetryableServer =
          status === 503 || (retryOnServerError && status !== undefined && status >= 500);

        if ((isRefreshableAuth || isRetryableServer) && attempt < maxRetries) {
          console.log(
            `[${functionName}] Attempt ${attempt + 1} failed with ${status}, retrying${isRefreshableAuth ? ' after session refresh' : ''}...`
          );

          if (isRefreshableAuth) {
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.error(`[${functionName}] Session refresh failed:`, refreshError);
            }
          }

          await sleep(backoffDelay(attempt));
          continue;
        }

        // 401 persistant : la session est réellement perdue. Sur un appel
        // explicitement sans session, le 401 est une erreur HTTP comme une autre.
        if (status === 401 && !skipSessionHandling) {
          const signedOut = await handleExpiredSession(functionName);
          return { data: null, error: normalized, authExpired: signedOut };
        }

        // 403 : l'utilisateur est bien authentifié, il n'a simplement pas le
        // droit d'appeler cette action. Le déconnecter serait un contresens
        // (cf. inventory-management / settings-management : « Admin role required »).
        if (status === 403) {
          console.warn(`[${functionName}] Forbidden (403): insufficient permissions`);
          return { data: null, error: normalized, forbidden: true };
        }

        break;
      }

      // HTTP 200 mais charge utile d'erreur : c'est un échec, pas un succès.
      const businessError = businessErrorFromPayload(functionName, data);
      if (businessError) {
        console.error(`[${functionName}] Business error returned with HTTP 200:`, businessError.message);
        return { data: (data ?? null) as T | null, error: businessError };
      }

      return { data: data as T, error: null };
    } catch (err) {
      // supabase-js ne lève quasiment jamais ici (il encapsule dans `error`),
      // mais une panne du client, un abort ou notre propre timeout passent là.
      const timedOut = isTimeoutError(err);
      lastError = timedOut ? err : await normalizeError(functionName, err);
      if (timedOut) {
        console.warn(`[${functionName}] Attempt ${attempt + 1} timed out after ${timeoutMs}ms`);
      }

      // Un timeout n'est pas rejoué par défaut : l'appel précédent est
      // peut-être encore en cours côté serveur, et le relancer produirait deux
      // exécutions concurrentes de la même écriture (cf. `retryOnTimeout`).
      const mayRetry = !timedOut || retryOnTimeout;
      if (mayRetry && attempt < maxRetries) {
        await sleep(backoffDelay(attempt));
        continue;
      }
      if (lastError.status === 401 && !skipSessionHandling) {
        const signedOut = await handleExpiredSession(functionName);
        return { data: null, error: lastError, authExpired: signedOut };
      }
      if (lastError.status === 403) {
        console.warn(`[${functionName}] Forbidden (403): insufficient permissions`);
        return { data: null, error: lastError, forbidden: true };
      }
      // `break` explicite : sans lui, un abandon décidé AVANT la dernière
      // tentative (timeout non rejoué) retomberait dans l'itération suivante de
      // la boucle et relancerait l'appel malgré tout.
      break;
    }
  }

  // Filet de sécurité : on ne sort jamais d'ici sans erreur, mais on garantit
  // que `error` est renseigné plutôt que de lever.
  return {
    data: null,
    error:
      lastError ||
      buildInvokeError(functionName, null, undefined, '', `[${functionName}] Edge function call failed`),
  };
}
